import React, { useCallback, useEffect, useState } from 'react';
import { FloatButton, Space, Tooltip, notification } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loadUsersInitial, loadUsersNext } from '../../store/adminSlice';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase-config';
import CreateUserModal from './CreateUserModal';
import GrantCompanyModal from './GrantCompanyModal';
import UsersTable from '../../components/Admin/UsersTable';
import UserCompanyAccessModal from './UserCompanyAccessModal';

const AdminUsersTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(s => s.admin.users.items);
  const uCursor = useAppSelector(s => s.admin.users.cursor);
  const uLoading = useAppSelector(s => s.admin.users.loading);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isGrantCompanyOpen, setIsGrantCompanyOpen] = useState(false);
  const [isCompanyAccessOpen, setIsCompanyAccessOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [permissionsByUser, setPermissionsByUser] = useState<Record<string, any>>({});

  const handleCreateSuccess = () => {
    dispatch(loadUsersInitial(50));
  };

  const handleGrantSuccess = (uid?: string) => {
    if (uid) {
      setPermissionsByUser(prev => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }
  };

  useEffect(() => {
    if (users.length === 0 && !uLoading) {
      dispatch(loadUsersInitial(50));
    }
  }, [dispatch, uLoading, users.length]);

  useEffect(() => {
    const missing = users
      .map(u => u?.id)
      .filter(uid => uid && !(uid in permissionsByUser));
    if (missing.length === 0) return;

    const loadPermissions = async () => {
      const nextMap: Record<string, any> = { ...permissionsByUser };
      const chunkSize = 10;
      for (let i = 0; i < missing.length; i += chunkSize) {
        const chunk = missing.slice(i, i + chunkSize);
        const q = query(collection(db, 'userPermissions'), where(documentId(), 'in', chunk));
        const snap = await getDocs(q);
        snap.docs.forEach(doc => {
          nextMap[doc.id] = doc.data();
        });
        chunk.forEach(uid => {
          if (!(uid in nextMap)) nextMap[uid] = null;
        });
      }
      setPermissionsByUser(nextMap);
    };

    loadPermissions().catch((e) => {
      notification.error({
        message: 'Failed to load permissions',
        description: e?.message || String(e),
        placement: 'topRight',
      });
    });
  }, [permissionsByUser, users]);

  const handleGrantCompany = useCallback((user: any) => {
    setSelectedUser(user);
    setIsGrantCompanyOpen(true);
  }, []);


  const handleViewCompanyAccess = useCallback((user: any) => {
    setSelectedUser(user);
    setIsCompanyAccessOpen(true);
  }, []);


  const handleLoadMore = useCallback(() => {
    if (uLoading || !uCursor) return;
    dispatch(loadUsersNext({ pageSize: 50, cursor: uCursor }));
  }, [dispatch, uCursor, uLoading]);

  return (
    <div style={{ position: 'relative' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <UsersTable
          users={users}
          permissionsByUser={permissionsByUser}
          loading={uLoading}
          onGrantCompany={handleGrantCompany}
          onLoadMore={handleLoadMore}
          onViewCompanyAccess={handleViewCompanyAccess}
        />
      </Space>

      <Tooltip title="Create user">
        <FloatButton
          icon={<PlusOutlined />}
          onClick={() => setIsCreateOpen(true)}
          type='primary'
          style={{ right: 24, bottom: 100 }}
        />
      </Tooltip>

      <CreateUserModal
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <GrantCompanyModal
        open={isGrantCompanyOpen}
        onCancel={() => setIsGrantCompanyOpen(false)}
        selectedUserEmail={selectedUser?.email}
        selectedUserId={selectedUser?.id}
        onSuccess={handleGrantSuccess}
      />

      <UserCompanyAccessModal
        open={isCompanyAccessOpen}
        onCancel={() => setIsCompanyAccessOpen(false)}
        user={selectedUser}
        permissions={selectedUser ? permissionsByUser[selectedUser.id] : null}
        onChanged={handleGrantSuccess}
      />

    </div>
  );
};

export default AdminUsersTab;
